import { useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Phone, WhatsappLogo, ArrowLeft, Clock, ShieldCheck } from "@phosphor-icons/react";

const PHONE = "9742119056";
const WA = `https://wa.me/91${PHONE}?text=Hi%2C%20I%20just%20submitted%20a%20booking%20request`;

export default function ThankYouPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const name = params.get("name") || "there";
  const service = params.get("service") || "service";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4 py-16 font-['Poppins']">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-lg w-full text-center"
      >
        {/* Success Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2, type: "spring", stiffness: 200 }}
          className="w-20 h-20 bg-[#22C55E]/10 rounded-full flex items-center justify-center mx-auto mb-6"
        >
          <CheckCircle size={48} weight="fill" className="text-[#22C55E]" />
        </motion.div>

        {/* Heading */}
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-3xl sm:text-4xl font-extrabold text-[#0F172A] tracking-tight mb-3"
        >
          Thank You, {name}!
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-slate-500 text-base mb-8 max-w-md mx-auto"
        >
          Your booking request has been received. Our technician will call you within <strong className="text-[#0F172A]">30 minutes</strong> to confirm your appointment.
        </motion.p>

        {/* Info Cards */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="grid grid-cols-2 gap-3 mb-8"
        >
          <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
            <Clock size={24} weight="duotone" className="text-[#FF6B00] mx-auto mb-2" />
            <p className="text-xs font-semibold text-[#0F172A]">Call Within</p>
            <p className="text-lg font-extrabold text-[#FF6B00]">30 Min</p>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
            <ShieldCheck size={24} weight="duotone" className="text-[#22C55E] mx-auto mb-2" />
            <p className="text-xs font-semibold text-[#0F172A]">Warranty</p>
            <p className="text-lg font-extrabold text-[#22C55E]">90 Days</p>
          </div>
        </motion.div>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="flex flex-col sm:flex-row gap-3 justify-center mb-6"
        >
          <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="thankyou-wa"
            className="bg-[#22C55E] text-white hover:bg-[#16A34A] font-bold text-base px-8 py-4 rounded-2xl shadow-xl shadow-green-200 transition-all flex items-center justify-center gap-2">
            <WhatsappLogo size={20} weight="fill" /> Chat on WhatsApp
          </a>
          <a href={`tel:${PHONE}`} data-testid="thankyou-call"
            className="bg-white text-[#0F172A] border border-slate-200 hover:border-[#FF6B00] font-bold text-base px-8 py-4 rounded-2xl transition-all flex items-center justify-center gap-2">
            <Phone size={20} weight="fill" /> Call {PHONE}
          </a>
        </motion.div>

        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.7 }}
          onClick={() => navigate(-1)}
          className="text-sm text-slate-400 hover:text-[#FF6B00] transition-colors inline-flex items-center gap-1.5"
        >
          <ArrowLeft size={14} /> Go Back
        </motion.button>
      </motion.div>
    </div>
  );
}
